// FILE: src/components/help/HelpCentre.jsx
// In-course help centre (MTO 6.6): getting started, step-by-step guides,
// troubleshooting, account & sign-in (incl. password reset), course rules,
// support. Searchable; each article opens on its own page.
import React, { useMemo, useState } from "react";
import { Box, Card, CardActionArea, CardContent, InputAdornment, Stack, TextField, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RocketLaunchOutlinedIcon from "@mui/icons-material/RocketLaunchOutlined";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import KeyOutlinedIcon from "@mui/icons-material/KeyOutlined";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { Link as RouterLink } from "react-router-dom";
import { getHelp } from "../../services/courseInfo";
import useAsync from "./useAsync";
import { EmptyState, ErrorState, LoadingState } from "../ui";

const ICONS = [
  [/getting started/i, RocketLaunchOutlinedIcon],
  [/question/i, HelpOutlineIcon],
  [/step/i, ListAltOutlinedIcon],
  [/trouble/i, BuildOutlinedIcon],
  [/account|sign/i, KeyOutlinedIcon],
  [/rule/i, GavelOutlinedIcon],
  [/support/i, SupportAgentOutlinedIcon]
];
const iconFor = (category) => (ICONS.find(([re]) => re.test(category)) || [null, HelpOutlineIcon])[1];

export function useHelp() {
  return useAsync(getHelp, []);
}

export default function HelpCentre({ basePath = "/help" }) {
  const { data, error, loading, reload } = useHelp();
  const [query, setQuery] = useState("");
  // Server shape: { categories: [{ id, title, description, articles: [...] }] }
  // in display order. Search filters inside each category.
  const categories = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (data?.categories || [])
      .map((c) => ({
        ...c,
        articles: (c.articles || []).filter(
          (a) => !q || `${a.title} ${a.summary || ""} ${c.title}`.toLowerCase().includes(q)
        )
      }))
      .filter((c) => c.articles.length > 0);
  }, [data, query]);
  const filtered = categories.flatMap((c) => c.articles);

  return (
    <Box>
      <TextField
        label="Search help articles"
        type="search"
        fullWidth
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchIcon aria-hidden /></InputAdornment> } }}
        sx={{ mb: 3, maxWidth: 640 }}
      />
      {loading && !data ? (
        <LoadingState variant="cards" label="Loading help articles…" />
      ) : error ? (
        <ErrorState error={error} title="We couldn't load the help centre" onRetry={reload} />
      ) : filtered.length === 0 ? (
        <EmptyState title="No articles match" description="Try different words, or contact student support and we'll help directly." />
      ) : (
        <Stack spacing={4} aria-live="polite">
          {categories.map((cat) => {
            const category = cat.title;
            const list = cat.articles;
            const Icon = iconFor(category);
            return (
              <Box component="section" key={cat.id} aria-labelledby={`cat-${cat.id}`}>
                <Stack direction="row" spacing={1.25} sx={{ alignItems: "center", mb: 1.5 }}>
                  <Icon aria-hidden sx={{ color: "primary.main" }} />
                  <Typography id={`cat-${cat.id}`} variant="h2" component="h2" sx={{ fontSize: "1.25rem" }}>
                    {category}
                  </Typography>
                </Stack>
                {cat.description && <Typography sx={{ color: "text.secondary", mb: 1.5, mt: -0.5 }}>{cat.description}</Typography>}
                <Box sx={{ display: "grid", gap: 1.5, gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" } }}>
                  {list.map((a) => (
                    <Card key={a.id}>
                      <CardActionArea component={RouterLink} to={`${basePath}/${a.id}`} sx={{ height: "100%" }}>
                        <CardContent>
                          <Typography component="h3" sx={{ fontWeight: 650, textDecoration: "underline", textUnderlineOffset: "0.18em", color: "primary.main" }}>
                            {a.title}
                          </Typography>
                          {a.summary && <Typography sx={{ color: "text.secondary", mt: 0.5 }}>{a.summary}</Typography>}
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  ))}
                </Box>
              </Box>
            );
          })}
        </Stack>
      )}
    </Box>
  );
}
